import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkUpdateDisputeHistoryRecordTagsApplicationErrors,
  BulkUpdateDisputeHistoryRecordTagsByFilterApplicationErrors,
  BulkUpdateDisputeHistoryRecordTagsByFilterOptions,
  BulkUpdateDisputeHistoryRecordTagsByFilterResponse,
  BulkUpdateDisputeHistoryRecordTagsOptions,
  BulkUpdateDisputeHistoryRecordTagsResponse,
  DisputeHistoryRecord,
  DisputeHistoryRecordQuery,
  DisputeHistoryRecordsQueryBuilder,
  QueryDisputeHistoryRecordsResponse,
  bulkUpdateDisputeHistoryRecordTags as universalBulkUpdateDisputeHistoryRecordTags,
  bulkUpdateDisputeHistoryRecordTagsByFilter as universalBulkUpdateDisputeHistoryRecordTagsByFilter,
  getDisputeHistoryRecord as universalGetDisputeHistoryRecord,
  queryDisputeHistoryRecords as universalQueryDisputeHistoryRecords,
  typedQueryDisputeHistoryRecords as universalTypedQueryDisputeHistoryRecords,
} from './payments-dispute-history-records-v1-dispute-history-record-dispute-history-records.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/payments' };

export function getDisputeHistoryRecord(
  httpClient: HttpClient
): GetDisputeHistoryRecordSignature {
  return (disputeHistoryRecordId: string) =>
    universalGetDisputeHistoryRecord(
      disputeHistoryRecordId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetDisputeHistoryRecordSignature {
  /**
   * Retrieves a dispute history record.
   * @param - ID of the dispute history record to retrieve.
   * @returns The retrieved dispute history record.
   */
  (disputeHistoryRecordId: string): Promise<
    NonNullablePaths<
      DisputeHistoryRecord,
      | `_id`
      | `disputeId`
      | `stage`
      | `status`
      | `chargeId`
      | `channel`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

export function queryDisputeHistoryRecords(
  httpClient: HttpClient
): QueryDisputeHistoryRecordsSignature {
  return () =>
    universalQueryDisputeHistoryRecords(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryDisputeHistoryRecordsSignature {
  /**
   * Creates a query to retrieve a list of dispute history records.
   *
   * The Query Dispute History Records method builds a query to retrieve a list of dispute history records and returns a `DisputeHistoryRecordsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the `find()` method.
   *
   * You can refine the query by chaining `DisputeHistoryRecordsQueryBuilder` methods onto the query. `DisputeHistoryRecordsQueryBuilder` methods enable you to filter, sort, and control the results that Query Dispute History Records returns.
   *
   * Query Dispute History Records has a default paging limit of 50, which you can override.
   *
   *  For a full description of the item object, see the object returned for the `items` property in `DisputeHistoryRecordsQueryResult`.
   */
  (): DisputeHistoryRecordsQueryBuilder;
}

export function typedQueryDisputeHistoryRecords(
  httpClient: HttpClient
): TypedQueryDisputeHistoryRecordsSignature {
  return (query: DisputeHistoryRecordQuery) =>
    universalTypedQueryDisputeHistoryRecords(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryDisputeHistoryRecordsSignature {
  /** */
  (query: DisputeHistoryRecordQuery): Promise<
    NonNullablePaths<
      QueryDisputeHistoryRecordsResponse,
      | `disputeHistoryRecords`
      | `disputeHistoryRecords.${number}._id`
      | `disputeHistoryRecords.${number}.disputeId`
      | `disputeHistoryRecords.${number}.stage`
      | `disputeHistoryRecords.${number}.status`
      | `disputeHistoryRecords.${number}.chargeId`
      | `disputeHistoryRecords.${number}.channel`,
      4
    >
  >;
}

export function bulkUpdateDisputeHistoryRecordTags(
  httpClient: HttpClient
): BulkUpdateDisputeHistoryRecordTagsSignature {
  return (ids: string[], options?: BulkUpdateDisputeHistoryRecordTagsOptions) =>
    universalBulkUpdateDisputeHistoryRecordTags(
      ids,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateDisputeHistoryRecordTagsSignature {
  /**
   * Updates tags on multiple dispute history records simultaneously using a list of dispute history record IDs.
   * If a tag appears in both assign and unassign lists, it will be assigned.
   * @param - List of dispute history records IDs to update tags for.
   */
  (ids: string[], options?: BulkUpdateDisputeHistoryRecordTagsOptions): Promise<
    NonNullablePaths<
      BulkUpdateDisputeHistoryRecordTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateDisputeHistoryRecordTagsApplicationErrors;
    }
  >;
}

export function bulkUpdateDisputeHistoryRecordTagsByFilter(
  httpClient: HttpClient
): BulkUpdateDisputeHistoryRecordTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateDisputeHistoryRecordTagsByFilterOptions
  ) =>
    universalBulkUpdateDisputeHistoryRecordTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateDisputeHistoryRecordTagsByFilterSignature {
  /**
   * Asynchronously updates tags on multiple dispute history records based on filter criteria.
   * Returns a job ID that can be used to track the operation status. If a tag appears in both assign and unassign lists, it will be assigned.
   * @param - Filter that determines which disputes to update tags for.
   *
   * An empty filter updates all disputes.
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateDisputeHistoryRecordTagsByFilterOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateDisputeHistoryRecordTagsByFilterResponse,
      `jobId`,
      2
    > & {
      __applicationErrorsType?: BulkUpdateDisputeHistoryRecordTagsByFilterApplicationErrors;
    }
  >;
}

export {
  AccountInfo,
  ActionEvent,
  ApplicationError,
  BulkActionMetadata,
  BulkUpdateDisputeHistoryRecordTagsByFilterOptions,
  BulkUpdateDisputeHistoryRecordTagsByFilterRequest,
  BulkUpdateDisputeHistoryRecordTagsByFilterResponse,
  BulkUpdateDisputeHistoryRecordTagsOptions,
  BulkUpdateDisputeHistoryRecordTagsRequest,
  BulkUpdateDisputeHistoryRecordTagsResponse,
  BulkUpdateDisputeHistoryRecordTagsResult,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DisputeChannel,
  DisputeHistoryRecord,
  DisputeHistoryRecordQuerySpec,
  DisputeHistoryRecordsQueryBuilder,
  DisputeHistoryRecordsQueryResult,
  DisputeStage,
  DisputeStatus,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  GetDisputeHistoryRecordRequest,
  GetDisputeHistoryRecordResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  MessageEnvelope,
  QueryDisputeHistoryRecordsRequest,
  QueryDisputeHistoryRecordsResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  TagList,
  Tags,
  TagsModified,
  WebhookIdentityType,
  utils,
} from './payments-dispute-history-records-v1-dispute-history-record-dispute-history-records.universal.js';
